/**
 * useQueryError hook
 *
 * Turns a `QueryError` (see `src/api/queryError.js`) into a user-facing
 * Persian error message. This repo has no i18n layer, so messages are plain
 * Persian literals; unknown errors fall back to a generic Persian message.
 */

/** Internal Persian error messages. */
const ERROR_MESSAGES = {
    networkError:
        'در برقراری ارتباط با سرور مشکلی پیش آمد. لطفاً اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.',
    serverError: 'خطای غیرمنتظرهای رخ داد. لطفاً دوباره تلاش کنید.',
    unknownError: 'خطای غیرمنتظرهای رخ داد. لطفاً دوباره تلاش کنید.',
    badRequest:
        'درخواست شما پردازش نشد. لطفاً صحت اطلاعات ارسالی را بررسی کنید و دوباره تلاش کنید.',
    unauthorized:
        'خطای احراز هویت رخ داد. لطفاً از ورود به حساب خود مطمئن شوید و دوباره تلاش کنید.',
    forbidden:
        'شما مجوز لازم برای انجام این عملیات را ندارید. لطفاً با پشتیبانی تماس بگیرید.',
    notFound: 'منبع موردنظر یافت نشد. لطفاً صفحه را تازهسازی کنید و دوباره تلاش کنید.',
    timeout: 'عملیات با وقفه زمانی مواجه شد. لطفاً دوباره تلاش کنید.',
    payloadTooLarge:
        'حجم فایل انتخابی شما بسیار زیاد است. لطفاً اندازه فایل را کاهش دهید.',
    unsupportedMediaType:
        'این پسوند فایل پشتیبانی نمیشود. لطفاً یک فایل معتبر بارگذاری کنید.',
    tooManyRequests:
        'به دلیل ارسال تعداد زیادی درخواست، دسترسی شما به طور موقت محدود شده است. لطفاً بعداً تلاش کنید.',
};

/**
 * Maps an HTTP status to its Persian error message.
 * @param {number} httpStatusCode - HTTP status.
 * @returns {string|undefined}
 */
const getErrorTitleFromHttpStatus = (httpStatusCode) => {
    switch (httpStatusCode) {
        case 400:
            return ERROR_MESSAGES.badRequest;
        case 401:
            return ERROR_MESSAGES.unauthorized;
        case 403:
            return ERROR_MESSAGES.forbidden;
        case 404:
            return ERROR_MESSAGES.notFound;
        case 408:
            return ERROR_MESSAGES.timeout;
        case 413:
            return ERROR_MESSAGES.payloadTooLarge;
        case 415:
            return ERROR_MESSAGES.unsupportedMediaType;
        case 429:
            return ERROR_MESSAGES.tooManyRequests;
        default:
            return undefined;
    }
};

/**
 * @param {object} queryError - A QueryError instance.
 * @returns {string} A Persian error message.
 */
const getErrorTitle = (queryError) => {
    switch (queryError.type) {
        case 'NetworkError':
            return ERROR_MESSAGES.networkError;
        case 'RequestError':
            return (
                getErrorTitleFromHttpStatus(queryError.status) ??
                ERROR_MESSAGES.unknownError
            );
        case 'ServerError':
            return ERROR_MESSAGES.serverError;
        default:
            return ERROR_MESSAGES.unknownError;
    }
};

/**
 * Hook returning a function that resolves a `QueryError` into a Persian message.
 * @returns {function} `(queryError) => string`
 */
export const useQueryError = () => {
    return (queryError) => getErrorTitle(queryError);
};